import { GetAdminVideoHostingQueryOutputProvider } from 'common/api/video-api-schema'
import { Video, VideoHosting, VideoLocale } from 'features/videos/service'
import { getKinescopeId, getVimeoHash, getVimeoId, getYouTubeVideoId } from 'utils/parseVideoUrl'

import {
  DEFAULT_LOCALE,
  mapProviderToLegacyHosting,
  trimOrUndefined,
  trimValue,
} from './videoMainForm.shared'
import { VideoHostingForm, VideoMainFormValues } from './videoMainForm.types'

const parseMetadata = (metadata: string, index: number) => {
  const normalizedMetadata = trimValue(metadata)

  if (!normalizedMetadata) {
    return { data: undefined }
  }

  try {
    const parsedMetadata = JSON.parse(normalizedMetadata)

    if (!parsedMetadata || Array.isArray(parsedMetadata) || typeof parsedMetadata !== 'object') {
      return {
        error: `Поле metadata в хостинге #${index + 1} должно содержать JSON-объект.`,
      }
    }

    return { data: parsedMetadata as Record<string, unknown> }
  } catch {
    return {
      error: `Поле metadata в хостинге #${index + 1} содержит невалидный JSON.`,
    }
  }
}

const normalizeHosting = (hosting: VideoHostingForm, index: number) => {
  const locale = trimValue(hosting.locale).toLowerCase()
  const sourceUrl = trimValue(hosting.sourceUrl)
  let hostingId = trimValue(hosting.hostingId)
  let hostingHash = trimValue(hosting.hostingHash)
  let url = trimValue(hosting.url)

  if (!locale) {
    return { error: `Укажи locale для хостинга #${index + 1}.` }
  }

  if (sourceUrl) {
    switch (hosting.provider) {
      case GetAdminVideoHostingQueryOutputProvider.YOUTUBE: {
        const parsedHostingId = getYouTubeVideoId(sourceUrl)

        if (parsedHostingId) {
          hostingId = parsedHostingId
        }
        if (!url) {
          url = sourceUrl
        }
        break
      }
      case GetAdminVideoHostingQueryOutputProvider.VIMEO: {
        const parsedHostingId = getVimeoId(sourceUrl)
        const parsedHostingHash = getVimeoHash(sourceUrl)

        if (parsedHostingId) {
          hostingId = parsedHostingId
        }
        if (parsedHostingHash) {
          hostingHash = parsedHostingHash
        }
        if (!url) {
          url = sourceUrl
        }
        break
      }
      case GetAdminVideoHostingQueryOutputProvider.KINESCOPE: {
        const parsedHostingId = getKinescopeId(sourceUrl)

        if (parsedHostingId && !hostingId) {
          hostingId = parsedHostingId
        }
        if (!url) {
          url = sourceUrl
        }
        break
      }
      case GetAdminVideoHostingQueryOutputProvider.VK: {
        if (!url) {
          url = sourceUrl
        }
        break
      }
    }
  }

  if (hosting.provider === GetAdminVideoHostingQueryOutputProvider.YOUTUBE && !hostingId) {
    return { error: `Для YouTube-хостинга #${index + 1} нужен hostingId или валидная ссылка.` }
  }

  if (
    hosting.provider === GetAdminVideoHostingQueryOutputProvider.VIMEO &&
    (!hostingId || !hostingHash)
  ) {
    return {
      error: `Для Vimeo-хостинга #${index + 1} нужны hostingId и hostingHash или валидная ссылка.`,
    }
  }

  if (
    (hosting.provider === GetAdminVideoHostingQueryOutputProvider.VK ||
      hosting.provider === GetAdminVideoHostingQueryOutputProvider.KINESCOPE) &&
    !url
  ) {
    return {
      error: `Для хостинга #${index + 1} с провайдером ${hosting.provider} нужен url или ссылка.`,
    }
  }

  const metadataResult = parseMetadata(hosting.metadata, index)

  if (metadataResult.error) {
    return { error: metadataResult.error }
  }

  const normalizedHosting: VideoHosting = {
    isPrimary: hosting.isPrimary,
    locale,
    provider: hosting.provider,
    ...(hosting.id ? { id: hosting.id } : {}),
    ...(hostingId ? { hostingId } : {}),
    ...(hostingHash ? { hostingHash } : {}),
    ...(url ? { url } : {}),
    ...(metadataResult.data ? { metadata: metadataResult.data } : {}),
  }

  return { hosting: normalizedHosting }
}

const pickLegacyLocale = (locales: VideoLocale[]) =>
  locales.find(locale => locale.locale === DEFAULT_LOCALE) ?? locales[0]

const pickLegacyPrimaryHosting = (hostings: VideoHosting[], locale: string) =>
  hostings.find(hosting => hosting.locale === locale && hosting.isPrimary) ?? hostings[0]

export const buildVideoPayload = (values: VideoMainFormValues) => {
  const localeCount = new Map<string, number>()
  const normalizedLocales = values.locales.map((locale, index) => {
    const normalizedLocale = trimValue(locale.locale).toLowerCase()
    const name = trimValue(locale.name)

    if (!normalizedLocale) {
      return { error: `Укажи locale для записи #${index + 1}.` }
    }

    if (!name) {
      return { error: `Укажи name для записи locale #${index + 1}.` }
    }

    localeCount.set(normalizedLocale, (localeCount.get(normalizedLocale) ?? 0) + 1)

    const normalizedVideoLocale: VideoLocale = {
      locale: normalizedLocale,
      name,
      ...(locale.id ? { id: locale.id } : {}),
      ...(trimOrUndefined(locale.description)
        ? { description: trimValue(locale.description) }
        : {}),
      ...(trimOrUndefined(locale.seoDescription)
        ? { seoDescription: trimValue(locale.seoDescription) }
        : {}),
    }

    return { locale: normalizedVideoLocale }
  })

  const localeError = normalizedLocales.find(item => item.error)?.error

  if (localeError) {
    return { error: localeError }
  }

  const locales = normalizedLocales
    .map(item => item.locale)
    .filter((locale): locale is VideoLocale => Boolean(locale))

  if (locales.length === 0) {
    return { error: 'Добавь хотя бы одну локаль.' }
  }

  const duplicateLocale = Array.from(localeCount.entries()).find(([, count]) => count > 1)?.[0]

  if (duplicateLocale) {
    return { error: `Локаль "${duplicateLocale}" в списке locales должна быть уникальной.` }
  }

  const normalizedHostings = values.hostings.map((hosting, index) =>
    normalizeHosting(hosting, index)
  )
  const hostingError = normalizedHostings.find(item => item.error)?.error

  if (hostingError) {
    return { error: hostingError }
  }

  const hostings = normalizedHostings
    .map(item => item.hosting)
    .filter((hosting): hosting is VideoHosting => Boolean(hosting))

  if (hostings.length === 0) {
    return { error: 'Добавь хотя бы один хостинг.' }
  }

  const primaryHostings = hostings.filter(hosting => hosting.isPrimary)
  const primaryLocaleCount = new Map<string, number>()

  for (const hosting of primaryHostings) {
    primaryLocaleCount.set(hosting.locale, (primaryLocaleCount.get(hosting.locale) ?? 0) + 1)
  }

  for (const locale of locales) {
    const primaryCount = primaryLocaleCount.get(locale.locale) ?? 0

    if (primaryCount !== 1) {
      return {
        error: `Для locale "${locale.locale}" должен быть ровно один primary-хостинг.`,
      }
    }
  }

  for (const hosting of hostings.filter(item => !item.isPrimary)) {
    if (!primaryLocaleCount.get(hosting.locale)) {
      return {
        error: `Secondary-хостинг locale "${hosting.locale}" можно добавить только вместе с primary той же locale.`,
      }
    }
  }

  const localeSet = new Set(locales.map(locale => locale.locale))
  const primaryLocaleSet = new Set(primaryHostings.map(hosting => hosting.locale))

  if (
    localeSet.size !== primaryLocaleSet.size ||
    Array.from(localeSet).some(locale => !primaryLocaleSet.has(locale))
  ) {
    return {
      error:
        'Множества locale в locales[] и locale у primary-хостингов должны полностью совпадать.',
    }
  }

  const legacyLocale = pickLegacyLocale(locales)
  const legacyPrimaryHosting = pickLegacyPrimaryHosting(hostings, legacyLocale.locale)

  const normalizedDurationInSeconds =
    typeof values.durationInSeconds === 'number' && Number.isFinite(values.durationInSeconds)
      ? Math.trunc(values.durationInSeconds)
      : null

  return {
    payload: {
      code: trimValue(values.code),
      description: legacyLocale.description ?? '',
      durationInSeconds: normalizedDurationInSeconds,
      hosting: mapProviderToLegacyHosting(legacyPrimaryHosting.provider),
      hostingHash: legacyPrimaryHosting.hostingHash ?? '',
      hostingId: legacyPrimaryHosting.hostingId ?? '',
      hostings,
      locales,
      mentor: trimValue(values.mentor),
      mentorId: values.mentorId,
      name: legacyLocale.name,
      publicVisibility: values.publicVisibility,
      seoDescription: trimOrUndefined(legacyLocale.seoDescription),
      slug: trimOrUndefined(values.slug),
      startDate: values.startDate,
      url: legacyPrimaryHosting.url ?? '',
    } satisfies Partial<Video>,
  }
}
